import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SOURCES, PAYMENT_TYPES } from "@/lib/constants";
import { BrregOrgField } from "@/components/BrregLookup";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";

const SOURCE_ORDER = ["efaktura", "vipps", "epost", "digipost", "eboks", "papir", "avtalegiro", "manuell"];

/** Definert utenfor komponenten slik at feltene ikke remonteres (og mister fokus/tilstand) ved hver tastetrykk. */
const Field = ({ id, label, error, children, hint }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    {children}
    {error ? <p className="text-xs text-destructive" data-testid={`manual-error-${id}`}>{error}</p> : hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
  </div>
);

export default function ManualInvoicePage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({ issuer_name: "", issuer_org_number: "", invoice_number: "", amount: "", invoice_date: "", due_date: "", account_number: "", kid: "", description: "", payment_type: "faktura", source: "efaktura", category_id: "ingen", notes: "" });

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data.items)).catch(() => {});
    api.get("/suppliers").then((r) => setSuppliers(r.data.items)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const pickSupplier = (name) => {
    const s = suppliers.find((x) => x.name === name);
    setForm((f) => ({ ...f, issuer_name: name, issuer_org_number: s?.org_number || f.issuer_org_number, account_number: !f.account_number && s?.account_numbers?.length ? s.account_numbers[s.account_numbers.length - 1] : f.account_number, category_id: s?.default_category_id || f.category_id }));
  };

  const validate = () => {
    const e = {};
    if (!form.issuer_name.trim()) e.issuer_name = "Leverandør må fylles ut";
    const amt = Number(String(form.amount).replace(/\s/g, "").replace(",", "."));
    if (form.amount === "" || Number.isNaN(amt)) e.amount = "Oppgi et gyldig beløp";
    if (!form.due_date) e.due_date = "Forfallsdato må fylles ut";
    if (form.payment_type === "faktura" && !form.account_number.replace(/\D/g, "")) e.account_number = "Kontonummer må fylles ut for vanlige fakturaer";
    if (form.account_number && form.account_number.replace(/\D/g, "").length !== 11) e.account_number = "Kontonummer skal ha 11 siffer";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      const payload = { ...form, amount: Number(String(form.amount).replace(/\s/g, "").replace(",", ".")), category_id: form.category_id === "ingen" ? null : form.category_id };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      const res = await api.post("/invoices/manual", payload);
      toast.success(res.data.status === "mulig_duplikat" ? "Registrert – men ligner på en eksisterende regning" : "Regningen er registrert");
      window.dispatchEvent(new Event("rp:refresh-notifications"));
      navigate(`/fakturaer/${res.data.id}`);
    } catch (err) {
      toast.error(errorMessage(err, "Kunne ikke registrere"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Registrer regning manuelt" description="For eFaktura, Vipps, AvtaleGiro eller regninger uten dokument. Skriv inn opplysningene slik de står i nettbanken." />
      <form onSubmit={submit} className="grid gap-4 lg:grid-cols-[1.6fr_1fr]" noValidate>
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Opplysninger</CardTitle><CardDescription>Felt merket med * er påkrevd.</CardDescription></CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2">
            <Field id="issuer_name" label="Leverandør *" error={errors.issuer_name}>
              <Input id="issuer_name" list="supplier-list" value={form.issuer_name} onChange={(e) => pickSupplier(e.target.value)} placeholder="f.eks. Kvinnherad Energi AS" className="h-11" data-testid="manual-issuer-input" />
              <datalist id="supplier-list">{suppliers.map((s) => <option key={s.id} value={s.name} />)}</datalist>
            </Field>
            <Field id="issuer_org_number" label="Organisasjonsnummer">
              <BrregOrgField id="issuer_org_number" value={form.issuer_org_number} onChange={(v) => setForm((f) => ({ ...f, issuer_org_number: v }))} onApply={(entity) => setForm((f) => ({ ...f, issuer_name: entity.name || f.issuer_name }))} autoApply={!form.issuer_name.trim()} inputClassName="h-11" testIdPrefix="manual" />
            </Field>
            <Field id="amount" label="Beløp *" error={errors.amount}><Input id="amount" value={form.amount} onChange={set("amount")} inputMode="decimal" placeholder="1 249,00" className="h-11 tnum" data-testid="manual-amount-input" /></Field>
            <Field id="due_date" label="Forfallsdato *" error={errors.due_date}><Input id="due_date" type="date" value={form.due_date} onChange={set("due_date")} className="h-11" data-testid="manual-due-input" /></Field>
            <Field id="account_number" label={form.payment_type === "faktura" ? "Kontonummer *" : "Kontonummer"} error={errors.account_number} hint="11 siffer"><Input id="account_number" value={form.account_number} onChange={set("account_number")} inputMode="numeric" placeholder="1234.56.78901" className="h-11 tnum" data-testid="manual-account-input" /></Field>
            <Field id="kid" label="KID"><Input id="kid" value={form.kid} onChange={set("kid")} inputMode="numeric" className="h-11 tnum" data-testid="manual-kid-input" /></Field>
            <Field id="invoice_number" label="Fakturanummer"><Input id="invoice_number" value={form.invoice_number} onChange={set("invoice_number")} className="h-11" data-testid="manual-invoice-number-input" /></Field>
            <Field id="invoice_date" label="Fakturadato"><Input id="invoice_date" type="date" value={form.invoice_date} onChange={set("invoice_date")} className="h-11" data-testid="manual-invoice-date-input" /></Field>
            <div className="sm:col-span-2"><Field id="description" label="Beskrivelse"><Input id="description" value={form.description} onChange={set("description")} placeholder="f.eks. Strøm september" className="h-11" data-testid="manual-description-input" /></Field></div>
            <div className="sm:col-span-2"><Field id="notes" label="Egne notater"><Textarea id="notes" rows={2} value={form.notes} onChange={set("notes")} data-testid="manual-notes-input" /></Field></div>
          </CardContent>
        </Card>
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Kilde og type</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <Field id="source" label="Hvor kom regningen fra?">
                <Select value={form.source} onValueChange={set("source")}>
                  <SelectTrigger id="source" className="h-11" data-testid="manual-source-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card">{SOURCE_ORDER.map((k) => <SelectItem key={k} value={k}>{SOURCES[k]}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field id="payment_type" label="Betalingstype" hint={form.payment_type === "avtalegiro" ? "Trekkes automatisk – kontonummer er ikke nødvendig." : undefined}>
                <Select value={form.payment_type} onValueChange={set("payment_type")}>
                  <SelectTrigger id="payment_type" className="h-11" data-testid="manual-payment-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card">{Object.entries(PAYMENT_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field id="category_id" label="Kategori">
                <Select value={form.category_id} onValueChange={set("category_id")}>
                  <SelectTrigger id="category_id" className="h-11" data-testid="manual-category-select"><SelectValue placeholder="Velg kategori" /></SelectTrigger>
                  <SelectContent className="bg-card"><SelectItem value="ingen">Uten kategori</SelectItem>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            </CardContent>
          </Card>
          <Button type="submit" className="w-full h-11" disabled={busy} data-testid="manual-submit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Registrer regning</Button>
        </div>
      </form>
    </div>
  );
}
